/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import UserDoc from '../../../../domain/models/UserDoc';
import updateUser, {
  UpdateUserFailure,
} from '../../../../domain/use_cases/users/updateUser';
import deleteUser from '../../../../domain/use_cases/users/deleteUser';
import getSubscriptionsByConstraints, {
  SUBSCRIPTION_FIRESTORE_IN_MAX,
} from '../../../../domain/use_cases/subscriptions/getSubscriptionsByConstraints';
import { documentId } from 'firebase/firestore';
import AuthenticationContext from '../../auth/AuthenticationContext';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { message } from 'antd';
import { Dayjs } from 'dayjs';
import { formatErrorMessage } from '../../../../core/utils/errorUtils';
import {
  countryCodes,
  cleanPhoneNumber,
  removeLeadingZeros,
} from '@leedpulse-monorepo/utils';
import {
  UserRole, 
  ContextStatus,
  EPhoneDisplayType,
} from '@leedpulse-monorepo/enums';
import getContactsByOwner from '../../../../domain/use_cases/contacts/getContactsByOwner';
import getScenariosByIds from '../../../../domain/use_cases/scenarios/getScenariosByIds';
import Scenario from '../../../../domain/models/Scenario';
import migrateContactsFromAnotherUser from '../../../../domain/use_cases/contacts/migrateContactsFromAnotherUser';
import { DEFAULT_AVAILABILITY_HOURS } from '../../../../domain/models/AvailabilityHours';
import changeUserPassword, {
  ChangeUserPasswordFailure,
} from '../../../../domain/use_cases/users/changeUserPassword';
 import { IAvailabilityHours } from '@leedpulse-monorepo/interfaces';
import { TUserCompanies, TUserSubscriptions, TDayOfWeek } from '@leedpulse-monorepo/types';
import getCompanies from '../../../../domain/use_cases/users/getCompanies';

export const USER_SUBSCRIPTIONS_PAGE_SIZE = 5;
const SUBSCRIPTION_SEARCH_DEBOUNCE_MS = 300;

export type UserSubscriptionListItem = {
  id: string;
  name: string;
  idPlan: string;
};

const UserEditionFormContext = createContext({
  wipUser: null as UserDoc | null,
  wipChangePassword: '',
  wipChangePasswordConfirmation: '',
  wipRequirePasswordChange: true,
  isUserChanged: false,
  changePasswordErrorMessage: '',
  companies: [] as { id: string; name: string }[],
  subscriptions: [] as UserSubscriptionListItem[],
  subscriptionsTotalCount: 0,
  subscriptionsTablePage: 1,
  subscriptionSearchDraft: '',
  setSubscriptionSearchDraft: (_: string) => {},
  subscriptionsListStatus: ContextStatus.IDLE,
  subscriptionMetaById: {} as Record<string, { name: string; idPlan: string }>,
  goToSubscriptionsPage: async (_: number) => {},
  scenarios: [] as Scenario[],
  subscriptionsHaveChanged: false,
  updateStatus: ContextStatus.IDLE,
  deleteStatus: ContextStatus.IDLE,
  errorMessage: '',
  initWithUser: (_: UserDoc) => {},
  submitUpdate: () => {},
  submitDelete: () => {},
  submitChangePassword: async () => false,
  onWipChangePasswordChange: (_: string) => {},
  onWipChangePasswordConfirmationChange: (_: string) => {},
  onWipRequirePasswordChangeChange: (_: boolean) => {},
  onAvatarChange: (_: string) => {},
  onFirstnameChange: (_: string) => {},
  onLastnameChange: (_: string) => {},
  onPhoneChange: (_: string) => {},
  onRoleChange: (_: UserRole) => {},
  onNotesChange: (_: string) => {},
  onIdCompaniesChange: (_: TUserCompanies) => {},
  onIdSubscriptionChange: (_: TUserSubscriptions) => {},
  onEmailsActivitiesChange: (_: boolean) => {},
  onEmailsNewsChange: (_: boolean) => {},
  onEmailsProspectInterestChange: (_: boolean) => {},
  onLegalEntityChange: (_: boolean) => {},
  handleResetPassword: () => {},
  handleContactsTransfer: (
    oldOwner: string,
    newOwner: string,
    scenarios: string[]
  ) => {},
  onDaysOffChange: (_: { start?: Dayjs | null; end?: Dayjs | null; message?: string }) => {},
  fetchSubscriptions: async () => {},
  fetchScenarios: async (owner: string) => {},
  status: ContextStatus.IDLE,
  selectedCountry: countryCodes[0],
  phoneNumber: '',
  onSelectedCountryCodeChange: (_: (typeof countryCodes)[0]) => {},
  onPhoneNumberChange: (_: string) => {},
  onPositionChange: (_: string) => {},
  onPhoneDisplayTypeChange: (_: EPhoneDisplayType) => {},
  onVoicemailTxtToSpeechChange: (_: string) => {},
  onVoipToggle: () => {},
  onRecordingAnnouncementToggle: () => {},
  onCalendarTasksCreationToggle: () => {},
  onSmsDisabledToggle: () => {},
  onDoNotDisturbToggle: () => {},
  onAvailabilityHoursToggle: () => {},
  onDayToggle: (_day: TDayOfWeek) => {},
  onAddSlot: (_day: TDayOfWeek) => {},
  onRemoveSlot: (_day: TDayOfWeek, _slotIndex: number) => {},
  onUpdateSlot: (_day: TDayOfWeek, _slotIndex: number, _field: 'start' | 'end', _value: string) => {},
});

export const UserEditionFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [wipUser, setWipUser] = useState(null as UserDoc | null);
  const [wipChangePassword, setWipChangePassword] = useState('');
  const [wipChangePasswordConfirmation, setWipChangePasswordConfirmation] =
    useState('');
  const [wipRequirePasswordChange, setWipRequirePasswordChange] = useState(true);

  const [initialUser, setInitialUser] = useState<UserDoc | null>(null);

  const [companies, setCompanies] = useState<
    { id: string; name: string }[]
  >([]);
  const [subscriptions, setSubscriptions] = useState<UserSubscriptionListItem[]>(
    []
  );
  const [subscriptionsTotalCount, setSubscriptionsTotalCount] = useState(0);
  const [subscriptionsTablePage, setSubscriptionsTablePage] = useState(1);
  const [subscriptionSearchDraft, setSubscriptionSearchDraft] = useState('');
  const [debouncedSubscriptionSearch, setDebouncedSubscriptionSearch] =
    useState('');
  const [subscriptionsListStatus, setSubscriptionsListStatus] = useState(
    ContextStatus.IDLE
  );
  const [subscriptionMetaById, setSubscriptionMetaById] = useState<
    Record<string, { name: string; idPlan: string }>
  >({});
  const [scenarios, setScenarios] = useState([] as Scenario[]);
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const subscriptionNameByIdRef = useRef<Record<string, string>>({});
  const subscriptionsFetchGenRef = useRef(0);
  const wipUserRef = useRef<UserDoc | null>(null);

  // Muter une ref pendant le rendu n'est pas sûr en rendu concurrent (le rendu
  // peut être abandonné). Les lecteurs sont tous des callbacks / effets
  // déclarés plus bas, donc cet effet s'exécute avant eux.
  useEffect(() => {
    wipUserRef.current = wipUser;
  }, [wipUser]);

  const [subscriptionsHaveChanged, setSubscriptionsHaveChanged] =
    useState(false);
  const [updateStatus, setUpdateStatus] = useState(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState(ContextStatus.IDLE);

  const [errorMessage, setErrorMessage] = useState('');
  const [changePasswordErrorMessage, setChangePasswordErrorMessage] =
    useState('');

  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [phoneNumber, setPhoneNumber] = useState('');

  const authContext = useContext(AuthenticationContext);

  const fetchSubscriptionsPage = useCallback(
    async (page: number, sortedIds: string[], searchQuery: string) => {
      const gen = ++subscriptionsFetchGenRef.current;
      setSubscriptionsListStatus(ContextStatus.LOADING);
      try {
        const q = searchQuery.trim().toLowerCase();
        let candidateIds = sortedIds;
        if (q) {
          const missing = sortedIds.filter(
            (id) => subscriptionNameByIdRef.current[id] === undefined
          );
          if (missing.length > 0) {
            const stubs: Record<string, string> = {};
            const metaPatch: Record<string, { name: string; idPlan: string }> =
              {};
            for (
              let i = 0;
              i < missing.length;
              i += SUBSCRIPTION_FIRESTORE_IN_MAX
            ) {
              const chunk = missing.slice(i, i + SUBSCRIPTION_FIRESTORE_IN_MAX);
              const { subscriptions: stubSubs } =
                await getSubscriptionsByConstraints(
                  [
                    { field: documentId(), op: 'in', value: chunk },
                    { field: documentId(), op: 'orderBy', value: 'asc' },
                  ],
                  { pageSize: chunk.length }
                );
              stubSubs.forEach((s) => {
                stubs[s.id] = s.name || '';
                metaPatch[s.id] = {
                  name: s.name || '',
                  idPlan: s.idPlan || '',
                };
              });
            }
            if (gen !== subscriptionsFetchGenRef.current) return;
            subscriptionNameByIdRef.current = {
              ...subscriptionNameByIdRef.current,
              ...stubs,
            };
            setSubscriptionMetaById((prev) => ({ ...prev, ...metaPatch }));
          }
          candidateIds = sortedIds.filter((id) =>
            (subscriptionNameByIdRef.current[id] || '')
              .toLowerCase()
              .includes(q)
          );
        }

        if (gen !== subscriptionsFetchGenRef.current) return;
        setSubscriptionsTotalCount(candidateIds.length);

        const start = (page - 1) * USER_SUBSCRIPTIONS_PAGE_SIZE;
        const pageIds = candidateIds.slice(
          start,
          start + USER_SUBSCRIPTIONS_PAGE_SIZE
        );

        if (pageIds.length === 0) {
          setSubscriptions([]);
        } else {
          const byId = new Map<string, UserSubscriptionListItem>();
          const metaPatch: Record<string, { name: string; idPlan: string }> =
            {};
          for (
            let i = 0;
            i < pageIds.length;
            i += SUBSCRIPTION_FIRESTORE_IN_MAX
          ) {
            const chunk = pageIds.slice(i, i + SUBSCRIPTION_FIRESTORE_IN_MAX);
            const { subscriptions: chunkSubs } =
              await getSubscriptionsByConstraints(
                [
                  { field: documentId(), op: 'in', value: chunk },
                  { field: documentId(), op: 'orderBy', value: 'asc' },
                ],
                { pageSize: chunk.length }
              );
            chunkSubs.forEach((sub) => {
              byId.set(sub.id, {
                id: sub.id,
                name: sub.name,
                idPlan: sub.idPlan,
              });
              metaPatch[sub.id] = {
                name: sub.name || '',
                idPlan: sub.idPlan || '',
              };
              subscriptionNameByIdRef.current[sub.id] = sub.name || '';
            });
          }
          const subs = pageIds
            .map((id) => byId.get(id))
            .filter((sub): sub is UserSubscriptionListItem => sub != null);
          if (gen !== subscriptionsFetchGenRef.current) return;
          setSubscriptions(subs);
          setSubscriptionMetaById((prev) => ({ ...prev, ...metaPatch }));
        }
        setSubscriptionsTablePage(page);
        setSubscriptionsListStatus(ContextStatus.SUCCESS);
      } catch (error) {
        if (gen !== subscriptionsFetchGenRef.current) return;
        console.error('Erreur lors de la récupération des abonnements :', error);
        setSubscriptionsListStatus(ContextStatus.ERROR);
      }
    },
    []
  );

  const userSubscriptionIdsKey =
    wipUser == null
      ? ''
      : Object.keys(wipUser.idSubscription || {}).sort().join(',');

  const goToSubscriptionsPage = useCallback(
    async (page: number) => {
      const wu = wipUserRef.current;
      if (wu == null) return;
      const sortedIds = Object.keys(wu.idSubscription || {}).sort();
      if (sortedIds.length === 0) return;
      await fetchSubscriptionsPage(page, sortedIds, debouncedSubscriptionSearch);
    },
    [debouncedSubscriptionSearch, fetchSubscriptionsPage]
  );

  const preloadSubscriptionMeta = useCallback(async (sortedIds: string[]) => {
    if (sortedIds.length === 0) {
      setSubscriptionMetaById({});
      return;
    }
    const meta: Record<string, { name: string; idPlan: string }> = {};
    const names: Record<string, string> = {};
    for (let i = 0; i < sortedIds.length; i += SUBSCRIPTION_FIRESTORE_IN_MAX) {
      const chunk = sortedIds.slice(i, i + SUBSCRIPTION_FIRESTORE_IN_MAX);
      const { subscriptions: chunkSubs } = await getSubscriptionsByConstraints(
        [
          { field: documentId(), op: 'in', value: chunk },
          { field: documentId(), op: 'orderBy', value: 'asc' },
        ],
        { pageSize: chunk.length }
      );
      chunkSubs.forEach((sub) => {
        meta[sub.id] = { name: sub.name || '', idPlan: sub.idPlan || '' };
        names[sub.id] = sub.name || '';
      });
    }
    subscriptionNameByIdRef.current = names;
    setSubscriptionMetaById(meta);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSubscriptionSearch(subscriptionSearchDraft.trim());
    }, SUBSCRIPTION_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [subscriptionSearchDraft]);

  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [lastSubSearch, setLastSubSearch] = useState(debouncedSubscriptionSearch);
  if (debouncedSubscriptionSearch !== lastSubSearch) {
    setLastSubSearch(debouncedSubscriptionSearch);
    setSubscriptionsTablePage(1);
  }

  useEffect(() => {
    const wu = wipUserRef.current;
    if (wu == null) return;
    const sortedIds = Object.keys(wu.idSubscription || {}).sort();
    if (sortedIds.length === 0) {
      setSubscriptionMetaById({});
      return;
    }
    void preloadSubscriptionMeta(sortedIds);
  }, [wipUser?.uid, userSubscriptionIdsKey, preloadSubscriptionMeta]);

  useEffect(() => {
    const wu = wipUserRef.current;
    if (wu == null) return;
    const sortedIds = Object.keys(wu.idSubscription || {}).sort();
    if (sortedIds.length === 0) {
      setSubscriptions([]);
      setSubscriptionsTotalCount(0);
      setSubscriptionsTablePage(1);
      setSubscriptionsListStatus(ContextStatus.SUCCESS);
      return;
    }
    void fetchSubscriptionsPage(1, sortedIds, debouncedSubscriptionSearch);
  }, [
    wipUser?.uid,
    userSubscriptionIdsKey,
    debouncedSubscriptionSearch,
    fetchSubscriptionsPage,
  ]);

  const isUserChanged = useMemo(() => {
    return wipUser?.firstname !== initialUser?.firstname ||
      wipUser?.lastname !== initialUser?.lastname ||
      wipUser?.phone !== initialUser?.phone ||
      wipUser?.role !== initialUser?.role ||
      wipUser?.idCompanies !== initialUser?.idCompanies ||
      wipUser?.idSubscription !== initialUser?.idSubscription ||
      wipUser?.position !== initialUser?.position ||
      wipUser?.phoneDisplayType !== initialUser?.phoneDisplayType;
  }, [wipUser, initialUser]);

  const initWithUser = async (user: UserDoc) => {
    subscriptionsFetchGenRef.current += 1;
    subscriptionNameByIdRef.current = {};
    setSubscriptionSearchDraft('');
    setDebouncedSubscriptionSearch('');
    setSubscriptions([]);
    setSubscriptionsTotalCount(0);
    setSubscriptionsTablePage(1);
    setSubscriptionMetaById({});
    setSubscriptionsListStatus(ContextStatus.IDLE);
    setWipUser(user);
    setInitialUser(user);
    setStatus(ContextStatus.LOADING);

    if (user.phone) {
      const phoneClean = user.phone.replace(/^\+/, '');

      const foundCountry = countryCodes.find((country) =>
        phoneClean.startsWith(country.code)
      );

      if (foundCountry) {
        setSelectedCountry(foundCountry);
        const numberWithoutCode = phoneClean.substring(
          foundCountry.code.length
        );
        const cleanedNumber = removeLeadingZeros(numberWithoutCode);
        setPhoneNumber(cleanedNumber);
      } else {
        const cleanedNumber = removeLeadingZeros(phoneClean);
        setPhoneNumber(cleanedNumber);
      }
    } else {
      setPhoneNumber('');
    }

    const bCompaniesToLoad = Object.keys(user.idCompanies || []).length > 0;
    if (bCompaniesToLoad) {
      try {
        const comp = await getCompanies(Object.keys(user.idCompanies));
        setCompanies(comp);
        setStatus(ContextStatus.SUCCESS);
      } catch (error) {
        console.error(
          'Erreur lors de la récupération des companies :',
          error
        );
        setStatus(ContextStatus.ERROR);
      }
    } else {
      setStatus(ContextStatus.SUCCESS);
    }
  };

  const fetchScenarios = async (owner: string) => {
    if (!wipUser) return;
    const contacts = await getContactsByOwner(owner);
    if (contacts) {
      const scenarioIds = [
        ...new Set(
          contacts.map((contact) => contact.contact.prospect_category)
        ),
      ];
      const scenarios = await getScenariosByIds(scenarioIds);
      setScenarios(scenarios);
    }
  };

  const fetchSubscriptions = async () => {
    const wu = wipUserRef.current;
    if (!wu || Object.keys(wu.idSubscription).length === 0) return;
    const sortedIds = Object.keys(wu.idSubscription).sort();
    await fetchSubscriptionsPage(
      subscriptionsTablePage,
      sortedIds,
      debouncedSubscriptionSearch
    );
  };

  const onWipChangePasswordChange = (password: string) => {
    setWipChangePassword(password);
  };
  const onWipChangePasswordConfirmationChange = (password: string) => {
    setWipChangePasswordConfirmation(password);
  };
  const onWipRequirePasswordChangeChange = (requirePasswordChange: boolean) => {
    setWipRequirePasswordChange(requirePasswordChange);
  };

  const onAvatarChange = (avatar: string) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ avatar }));
  };

  const onFirstnameChange = (firstname: string) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ firstname }));
  };

  const onLastnameChange = (lastname: string) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ lastname }));
  };

  const onPhoneChange = (phone: string) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ phone }));
  };

  const onSelectedCountryCodeChange = (country: (typeof countryCodes)[0]) => {
    setSelectedCountry(country);

    const cleanedNumber = removeLeadingZeros(phoneNumber);
    const fullPhone = country.code + cleanedNumber;

    if (wipUser !== null) {
      setWipUser(wipUser.copyWith({ phone: fullPhone }));
    }
  };

  const onPhoneNumberChange = (value: string) => {
    const digitsOnly = cleanPhoneNumber(value);

    const withoutCountryCode = digitsOnly.startsWith(selectedCountry.code)
      ? digitsOnly.substring(selectedCountry.code.length)
      : digitsOnly;

    const cleanedNumber = removeLeadingZeros(withoutCountryCode);

    setPhoneNumber(cleanedNumber);

    const fullPhone = selectedCountry.code + cleanedNumber;

    if (wipUser !== null) {
      setWipUser(wipUser.copyWith({ phone: fullPhone }));
    }
  };

  const onPositionChange = (position: string) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ position }));
  };

  const onPhoneDisplayTypeChange = (phoneDisplayType: EPhoneDisplayType) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ phoneDisplayType }));
  };

  const onVoicemailTxtToSpeechChange = (voicemailTxtToSpeech: string) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ voicemailTxtToSpeech }));
  };

  const onVoipToggle = () => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ voip_enabled: !wipUser.voip_enabled }));
  };

  const onRecordingAnnouncementToggle = () => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ recordingAnnouncementEnabled: !wipUser.recordingAnnouncementEnabled }));
  };

  const onCalendarTasksCreationToggle = () => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ calendarTasksCreationEnabled: !wipUser.calendarTasksCreationEnabled }));
  };

  const onSmsDisabledToggle = () => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ smsDisabled: !wipUser.smsDisabled }));
  };

  const onDoNotDisturbToggle = () => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ doNotDisturb: !wipUser.doNotDisturb }));
  };

  const onAvailabilityHoursToggle = () => {
    if (wipUser === null) return;
    if (wipUser.availabilityHours === null) {
      const initialized: IAvailabilityHours = { ...DEFAULT_AVAILABILITY_HOURS, enabled: true };
      setWipUser(wipUser.copyWith({ availabilityHours: initialized }));
      return;
    }
    const updated: IAvailabilityHours = {
      ...wipUser.availabilityHours,
      enabled: !wipUser.availabilityHours.enabled,
    };
    setWipUser(wipUser.copyWith({ availabilityHours: updated }));
  };

  const onDayToggle = (day: TDayOfWeek) => {
    if (wipUser === null || wipUser.availabilityHours === null) return;
    const current = wipUser.availabilityHours.schedule[day];
    const enabling = !current.enabled;
    const schedule = { ...wipUser.availabilityHours.schedule };
    schedule[day] = {
      ...current,
      enabled: enabling,
      slots: enabling && current.slots.length === 0
        ? [{ start: '09:00', end: '18:00' }]
        : current.slots,
    };
    setWipUser(wipUser.copyWith({ availabilityHours: { ...wipUser.availabilityHours, schedule } }));
  };

  const onAddSlot = (day: TDayOfWeek) => {
    if (wipUser === null || wipUser.availabilityHours === null) return;
    const daySchedule = wipUser.availabilityHours.schedule[day];
    if (daySchedule.slots.length >= 2) return;
    const lastSlot = daySchedule.slots[daySchedule.slots.length - 1];
    let newStart = '09:00';
    let newEnd = '18:00';
    if (lastSlot) {
      const [h] = lastSlot.end.split(':').map(Number);
      const startH = Math.min(h + 1, 23);
      const endH = Math.min(startH + 2, 23);
      newStart = `${String(startH).padStart(2, '0')}:00`;
      newEnd = `${String(endH).padStart(2, '0')}:00`;
    }
    const schedule = { ...wipUser.availabilityHours.schedule };
    schedule[day] = { ...daySchedule, slots: [...daySchedule.slots, { start: newStart, end: newEnd }] };
    setWipUser(wipUser.copyWith({ availabilityHours: { ...wipUser.availabilityHours, schedule } }));
  };

  const onRemoveSlot = (day: TDayOfWeek, slotIndex: number) => {
    if (wipUser === null || wipUser.availabilityHours === null) return;
    const daySchedule = wipUser.availabilityHours.schedule[day];
    const schedule = { ...wipUser.availabilityHours.schedule };
    schedule[day] = { ...daySchedule, slots: daySchedule.slots.filter((_, i) => i !== slotIndex) };
    setWipUser(wipUser.copyWith({ availabilityHours: { ...wipUser.availabilityHours, schedule } }));
  };

  const onUpdateSlot = (day: TDayOfWeek, slotIndex: number, field: 'start' | 'end', value: string) => {
    if (wipUser === null || wipUser.availabilityHours === null) return;
    const daySchedule = wipUser.availabilityHours.schedule[day];
    const newSlots = daySchedule.slots.map((slot, i) =>
      i === slotIndex ? { ...slot, [field]: value } : slot
    );
    const schedule = { ...wipUser.availabilityHours.schedule };
    schedule[day] = { ...daySchedule, slots: newSlots };
    setWipUser(wipUser.copyWith({ availabilityHours: { ...wipUser.availabilityHours, schedule } }));
  };

  const onRoleChange = (role: UserRole) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ role }));
  };

  const onNotesChange = (notes: string) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ notes }));
  };

  const onIdCompaniesChange = (oIdCompanies: TUserCompanies) => {
    if (wipUser === null) return;
    const idCompanies = wipUser.idCompanies;
    setWipUser(wipUser.copyWith({ idCompanies: {...idCompanies, ...oIdCompanies} }));
  };

  const onIdSubscriptionChange = (oIdSubscription: TUserSubscriptions) => {
    if (wipUser === null) return;
    const idSubscription = wipUser.idSubscription;
    setWipUser(wipUser.copyWith({ idSubscription: {...idSubscription, ...oIdSubscription} }));
  };

  const onDaysOffChange = ({
    start,
    end,
    message,
  }: {
    start?: Dayjs | null;
    end?: Dayjs | null;
    message?: string;
  }) => {
    if (!wipUser) {
      console.error('wipUser is undefined.');
      return;
    }
    setWipUser(
      wipUser.copyWith({
        daysOffStart: start !== undefined ? start : wipUser.daysOffStart,
        daysOffEnd: end !== undefined ? end : wipUser.daysOffEnd,
        daysOffMessage: message !== undefined ? message : wipUser.daysOffMessage,
      })
    );
  };

  const onEmailsActivitiesChange = (activities: boolean) => {
    if (wipUser === null) return;
    const updatedEmails = wipUser.emails.copyWith({ activities });
    setWipUser(
      wipUser.copyWith({
        emails: updatedEmails,
      })
    );
  };

  const onEmailsNewsChange = (news: boolean) => {
    if (wipUser === null) return;
    const updatedEmails = wipUser.emails.copyWith({ news });
    setWipUser(
      wipUser.copyWith({
        emails: updatedEmails,
      })
    );
  };

  const onEmailsProspectInterestChange = (prospect_interest: boolean) => {
    if (wipUser === null) return;
    const updatedEmails = wipUser.emails.copyWith({ prospect_interest });
    setWipUser(
      wipUser.copyWith({
        emails: updatedEmails,
      })
    );
  };

  const onLegalEntityChange = (legalEntity: boolean) => {
    if (wipUser === null) return;
    setWipUser(wipUser.copyWith({ legalEntity }));
  };

  const submitUpdate = async () => {
    if (wipUser === null || authContext.authentication == null) return;
    setUpdateStatus(ContextStatus.LOADING);
    try {
      await updateUser(wipUser, authContext.authentication);
      setSubscriptionsHaveChanged(false);
      setUpdateStatus(ContextStatus.SUCCESS);
      setInitialUser(wipUser);
    } catch (e) {
      if (e instanceof UpdateUserFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  const submitChangePassword = async () => {
    if (authContext.authentication === null || !wipUser) return false;
    if (!wipChangePassword || !wipChangePasswordConfirmation) {
      setChangePasswordErrorMessage(
        'Veuillez entrer un mot de passe et une confirmation'
      );
      return false;
    }

    if (wipChangePassword.length < 8) {
      setChangePasswordErrorMessage(
        'Le mot de passe doit contenir au moins 8 caractères'
      );
      return false;
    }

    if (wipChangePassword.length > 100) {
      setChangePasswordErrorMessage(
        'Le mot de passe ne doit pas contenir plus de 100 caractères'
      );
      return false;
    }

    if (wipChangePassword !== wipChangePasswordConfirmation) {
      setChangePasswordErrorMessage('Les mots de passe ne correspondent pas');
      return false;
    }

    setChangePasswordErrorMessage('');

    setUpdateStatus(ContextStatus.LOADING);

    try {
      await changeUserPassword(
        wipChangePassword,
        authContext.authentication,
        wipUser.uid,
        wipRequirePasswordChange
      );
      setUpdateStatus(ContextStatus.SUCCESS);

      return true;
    } catch (e) {
      if (e instanceof ChangeUserPasswordFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setUpdateStatus(ContextStatus.ERROR);
      return false;
    }
  };

  const submitDelete = async () => {
    if (wipUser === null || authContext.authentication == null) return;
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deleteUser(wipUser, authContext.authentication);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const handleResetPassword = async () => {
    if (wipUser?.email) {
      setUpdateStatus(ContextStatus.LOADING);
      const auth = getAuth();
      try {
        await sendPasswordResetEmail(auth, wipUser.email);
        message.success('Email de réinitialisation envoyé avec succès !');
        setUpdateStatus(ContextStatus.SUCCESS);
      } catch (error) {
        console.error(
          "Erreur lors de l'envoi de l'e-mail de réinitialisation : ",
          error
        );
        message.error(
          "Erreur lors de l'envoi de l'e-mail de réinitialisation."
        );
        setUpdateStatus(ContextStatus.ERROR);
      }
    } else {
      message.error("L'utilisateur n'a pas d'adresse e-mail valide.");
    }
  };

  const handleContactsTransfer = async (
    oldOwner: string,
    newOwner: string,
    scenarios: string[]
  ) => {
    if (authContext.authentication === null) return;
    setUpdateStatus(ContextStatus.LOADING);
    try {
      if (scenarios.length > 0) {
        for (const scenario of scenarios) {
          await migrateContactsFromAnotherUser(
            oldOwner,
            newOwner,
            scenario,
            authContext.authentication
          );
        }
      } else {
        await migrateContactsFromAnotherUser(
          oldOwner,
          newOwner,
          '',
          authContext.authentication
        );
      }
      setUpdateStatus(ContextStatus.SUCCESS);
    } catch (error) {
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  return (
    <UserEditionFormContext.Provider
      value={{
        wipUser,
        wipChangePassword,
        wipChangePasswordConfirmation,
        wipRequirePasswordChange,
        isUserChanged,
        changePasswordErrorMessage,
        companies,
        subscriptions,
        subscriptionsTotalCount,
        subscriptionsTablePage,
        subscriptionSearchDraft,
        setSubscriptionSearchDraft,
        subscriptionsListStatus,
        subscriptionMetaById,
        goToSubscriptionsPage,
        scenarios,
        subscriptionsHaveChanged,
        updateStatus,
        deleteStatus,
        errorMessage,
        initWithUser,
        onAvatarChange,
        submitUpdate,
        submitDelete,
        submitChangePassword,
        onWipChangePasswordChange,
        onWipChangePasswordConfirmationChange,
        onWipRequirePasswordChangeChange,
        onFirstnameChange,
        onLastnameChange,
        onPhoneChange,
        onRoleChange,
        onNotesChange,
        onIdCompaniesChange,
        onIdSubscriptionChange,
        onDaysOffChange,
        onEmailsActivitiesChange,
        onEmailsNewsChange,
        onEmailsProspectInterestChange,
        onLegalEntityChange,
        handleResetPassword,
        handleContactsTransfer,
        fetchSubscriptions,
        fetchScenarios,
        status,
        selectedCountry,
        phoneNumber,
        onSelectedCountryCodeChange,
        onPhoneNumberChange,
        onPositionChange,
        onPhoneDisplayTypeChange,
        onVoicemailTxtToSpeechChange,
        onVoipToggle,
        onRecordingAnnouncementToggle,
        onCalendarTasksCreationToggle,
        onSmsDisabledToggle,
        onDoNotDisturbToggle,
        onAvailabilityHoursToggle,
        onDayToggle,
        onAddSlot,
        onRemoveSlot,
        onUpdateSlot,
      }}
    >
      {children}
    </UserEditionFormContext.Provider>
  );
};

export default UserEditionFormContext;
